import React from "react";
import { useTranslation } from "react-i18next";
import LocationCascader from "./LocationCascader";
import defaultMerchantImage from "assets/MerchantDefault.jpeg";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import dayjs from "dayjs";
import { useWatch } from "react-hook-form";

const PAGE_LOCALE_KEY = "page.merchants.";
const FORM_LABEL_KEY =
  PAGE_LOCALE_KEY + "merchantDetail.info.merchantInfo.fields";
const COUNTRY_OPTIONS = [
  {
    key: "hk",
    title: "Hong Kong",
    value: "HK",
  },
  // Deprecated option, commented for possible future use
  // {
  //   key: "sg",
  //   title: "Singapore",
  //   value: "SG",
  // },
]

const MerchantInfoSession = ({
  formProps,
  isCreate,
  isEdit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [name, nameSecondaryLang, ownerAddress1, ownerAddress2, ownerCountry] = useWatch({ control: formProps.control, name: ["name", "nameSecondaryLang", "ownerAddress1", "ownerAddress2", "ownerCountry"] });

  const isDisabled = !isEdit || isLoading;

  const alignStoreInfo = () => {
    const { setValue } = formProps;

    //Validate the field again if the value is copied over, mainly to remove mandatory messages, but copied empty values are not validated
    setValue("storeName", name, { shouldValidate: name });
    setValue("storeNameSecondaryLang", nameSecondaryLang, { shouldValidate: nameSecondaryLang });
    setValue("address1", ownerAddress1, { shouldValidate: ownerAddress1 });
    setValue("storeAddressSecondaryLang", ownerAddress2, { shouldValidate: ownerAddress2 });
    setValue("country", ownerCountry, { shouldValidate: ownerCountry });
  };


  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    storeName: {
      fieldType: "textInput",
      fieldName: "storeName",
      label: t(`${FORM_LABEL_KEY}.storeName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 22,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    storeNameSecondaryLang: {
      fieldType: "textInput",
      fieldName: "storeNameSecondaryLang",
      label: t(`${FORM_LABEL_KEY}.storeNameSecondaryLang`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: ["noLeadingWhitespace", "noQuotesOrBackticks"]
      },
    },
    address1: {
      fieldType: "textInput",
      fieldName: "address1",
      label: t(`${FORM_LABEL_KEY}.address1`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "onlyAlphabetNumAndSpaces",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    storeAddressSecondaryLang: {
      fieldType: "textInput",
      fieldName: "storeAddressSecondaryLang",
      label: t(`${FORM_LABEL_KEY}.storeAddressSecondaryLang`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "onlyAlphabetNumChineseAndSpaces",
        ],
      },
    },

    country: {
      fieldType: "selection",
      fieldName: "country",
      label: t(`${FORM_LABEL_KEY}.country`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        options: COUNTRY_OPTIONS,
        defaultValue: "HK",
        // Deprecated logic for non-HK countries (e.g. Singapore), commented for possible future use
        // defaultValueTriggerUpdate: true,
        // customOnChangeFn: (value, { setValue }) => {
        //   if (value !== "HK") {
        //     setValue("storeArea", "N/A");
        //     setValue("storeDistrict", "N/A");
        //     setValue("storeSubdistrict", "N/A");
        //   }
        // },
      },
    },

    reportEmail: {
      fieldType: "textInput",
      fieldName: "reportEmail",
      label: t(`${FORM_LABEL_KEY}.reportEmail`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: ["validEmail"]
      }
    },
    businessRegistration: {
      fieldType: "textInput",
      fieldName: "businessRegistration",
      label: t(`${FORM_LABEL_KEY}.businessRegistration`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: ["validBusinessRegNo"]
      },
    },

    businessRegistrationExpiryDate: {
      fieldType: "datePicker",
      fieldName: "businessRegistrationExpiryDate",
      label: t(`${FORM_LABEL_KEY}.businessRegistrationExpiryDate`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: dayjs().add(1, "day").toDate(),
        minDate: dayjs().add(1, "day"),
        rules: ["validDate"]
      },
    },

    merchantLogo: {
      fieldType: "imagePicker",
      fieldName: "logo",
      label: "Merchant Logo",
      mandatory: true,
      disabled: isDisabled,
      config: {
        labelRemarks: `(Maximum image size: 2MB; Format: jpg and png only; Resolution:
          250x250px - 1024x1024px)`,
        importantRemarks: `*** Use a square image to prevent distortion. ***`,
        defaultImage: isCreate && defaultMerchantImage,
        allowedMimeTypes: ["image/jpeg", "image/png"],
        maxSize: 2 * 1024 * 1024,
        dimension: {
          widthLimit: [250, 1024],
          heightLimit: [250, 1024],
        }
      }
    }
  }

  return (
    <FormBuilder.Section
      title={t(`${PAGE_LOCALE_KEY}merchantDetail.info.merchantInfo.title`)}
      titleVariant="h3"
      extraConfigs={[
        {
          componentType: "button",
          config: {
            title: t(`${PAGE_LOCALE_KEY}merchantDetail.info.merchantInfo.buttonTitle`),
            onClick: () => alignStoreInfo(true),
            disabled: isDisabled,
          }
        }
      ]}>
      <FormBuilder.Row itemsPerRow={2}>
        {FormBuilder.buildFields({
          formProps,
          fields: [
            fieldsConfig.storeName,
            fieldsConfig.storeNameSecondaryLang,
            fieldsConfig.address1,
            fieldsConfig.storeAddressSecondaryLang
          ]
        })}
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {FormBuilder.buildFields({
          formProps,
          fields: [
            fieldsConfig.country,
          ]
        })}
      </FormBuilder.Row>

      <LocationCascader
        formProps={formProps}
        isEdit={isEdit}
        isLoading={isLoading}
      />

      <FormBuilder.Row itemsPerRow={2}>
        {FormBuilder.buildFields({
          formProps,
          fields: [
            fieldsConfig.reportEmail,
            fieldsConfig.businessRegistration
          ]
        })}
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {FormBuilder.buildFields({
          formProps,
          fields: [
            fieldsConfig.businessRegistrationExpiryDate,
          ]
        })}
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={1}>
        {FormBuilder.buildFields({
          formProps,
          fields: [
            fieldsConfig.merchantLogo,
          ]
        })}
      </FormBuilder.Row>
    </FormBuilder.Section>
  );
};
export default React.memo(MerchantInfoSession);
